import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Search,
    Filter,
    Download,
    Send,
    Eye,
    Edit,
    Trash2,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    Mail,
    MessageCircle,
    FileText,
    Calendar,
    DollarSign,
    Users,
    Building,
    Phone,
    ExternalLink,
    Copy,
    Settings,
    ChevronLeft,
    ChevronRight,
    UserPlus,
    Zap
} from 'lucide-react';
import LineItemRow from './LineItemRow';

const InvoiceManagement = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [syncing, setSyncing] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [stats, setStats] = useState({});
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        limit: 20,
        has_next: false,
        has_previous: false
    });

    // NEW: Customer creation states
    const [creatingCustomer, setCreatingCustomer] = useState(null);
    const [checkingCustomer, setCheckingCustomer] = useState(null);
    const [customerStatus, setCustomerStatus] = useState({});

    // Form state
    const [formData, setFormData] = useState(() => {
        // Get today's date safely for initial state
        const today = new Date();

        // Validate that today is a valid date
        if (isNaN(today.getTime())) {
            console.error('Invalid current date');
            return {
                company_id: '',
                customer_name: '',
                invoice_date: '',
                due_date: '',
                place_of_supply: '',
                payment_terms_days: 30,
                notes: '',
                terms_conditions: '',
                line_items: [
                    {
                        zoho_item_id: '',
                        item_name: '',
                        description: '',
                        quantity: 1,
                        unit_price: 0,
                        discount_percentage: 0,
                        tax_rate: 18
                    }
                ],
                auto_sync_zoho: false,
                send_notifications: false
            };
        }

        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayFormatted = `${year}-${month}-${day}`;

        return {
            company_id: '',
            customer_name: '',
            invoice_date: todayFormatted,
            due_date: '',
            place_of_supply: '',
            payment_terms_days: 30,
            notes: '',
            terms_conditions: '',
            line_items: [
                {
                    zoho_item_id: '',
                    item_name: '',
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    discount_percentage: 0,
                    tax_rate: 18
                }
            ],
            auto_sync_zoho: false,
            send_notifications: false
        };
    });

    // Filters
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        company_id: '',
        page: 1,
        limit: 20,
        sort_by: 'created_at',
        sort_order: 'DESC'
    });

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [updating, setUpdating] = useState(false);

    const [zohoItems, setZohoItems] = useState([]);

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    // Organization state
    const [currentOrganization, setCurrentOrganization] = useState({
        organization_id: '',
        name: ''
    });

    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    // API base URL
    const API_BASE = 'https://api.zapbooksai.subraps.com/api';

    // EXACT same pattern as RecurringExpensesIntegrationManager
    const canEdit = useCallback((invoice) => {
        const status = invoice.approver_status || invoice.approvalInfo?.status;
        const isSynced = !!invoice.zoho_invoice_id;
        console.log('Edit check for invoice', invoice.id, 'status:', status);
        // Allow editing if approved (regardless of sync status)
        return status === 'approved' && !isSynced;
    }, []);

    const canSync = useCallback((invoice) => {
        const status = invoice.approver_status || invoice.approvalInfo?.status;
        const isSynced = !!invoice.zoho_invoice_id;
        console.log('Sync check for invoice', invoice.id, 'status:', status, 'isSynced:', isSynced);
        // Only allow syncing if approved AND not already synced
        return status === 'approved' && !isSynced;
    }, []);

    const getApprovalStatusDisplay = useCallback((invoice) => {
        // FIXED: Use actual database status, no fallback to user_type
        const status = invoice.approver_status || invoice.approvalInfo?.status;

        console.log(`🏷️ Status display for invoice ${invoice.id}: 
            - Database status: ${invoice.approver_status}
            - User type: ${invoice.user_type}
            - Final status: ${status}`);

        if (status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {invoice.approved_by && <span className="ml-1">by {invoice.approved_by}</span>}
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                </span>
            );
        }

        // Default to pending approval
        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Clock className="w-3 h-3 mr-1" />
                Pending Approval
            </span>
        );
    }, []);

    // Update the openEditModal function - add this to your component
    const openEditModal = async (invoiceId) => {
        try {
            const response = await fetch(`${API_BASE}/invoices/${invoiceId}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            if (data.success) {
                const invoice = data.data;
                // Check if invoice can be edited
                if (!canEdit(invoice)) {
                    alert('This invoice has already been synced to Zoho Books and cannot be edited.');
                    return;
                }
                setEditingInvoice(invoice);

                // FIXED: Use the corrected date formatting
                setFormData({
                    company_id: invoice.company_id || '',
                    customer_name: invoice.customer_name || '',
                    invoice_date: formatDateForInput(invoice.invoice_date),
                    due_date: formatDateForInput(invoice.due_date),
                    place_of_supply: invoice.place_of_supply || '',
                    payment_terms_days: invoice.payment_terms_days,
                    notes: invoice.notes || '',
                    terms_conditions: invoice.terms_conditions || '',
                    line_items: invoice.line_items && invoice.line_items.length > 0
                        ? invoice.line_items.map(item => ({
                            ...item, // Spread first!
                            zoho_item_id: item.zoho_item_id ?? '', // Use nullish coalescing to cover undefined/null
                            item_name: item.item_name || '',
                            description: item.description || '',
                            quantity: item.quantity || 1,
                            unit_price: item.unit_price || 0,
                            discount_percentage: item.discount_percentage || 0,
                            tax_rate: item.tax_rate || 18
                        }))
                        : [{
                            zoho_item_id: '',
                            item_name: '',
                            description: '',
                            quantity: 1,
                            unit_price: 0,
                            discount_percentage: 0,
                            tax_rate: 18
                        }],
                    auto_sync_zoho: false,
                    send_notifications: false
                });

                setShowEditModal(true);
            } else {
                throw new Error(data.message || 'Error fetching invoice details');
            }
        } catch (error) {
            handleApiError(error, 'Error fetching invoice for edit');
        }
    };

    // Function to update existing invoice
    const updateInvoice = async () => {
        try {
            setUpdating(true);
            setError(null);

            // Validate required fields
            if (!formData.company_id || !formData.customer_name || !formData.line_items.length) {
                throw new Error('Please fill in all required fields');
            }

            // Validate line items
            for (let i = 0; i < formData.line_items.length; i++) {
                const item = formData.line_items[i];
                if (!item.item_name || !item.quantity || item.unit_price === undefined) {
                    throw new Error(`Please fill in all fields for line item ${i + 1}`);
                }
            }

            // FIXED: Format dates properly for database
            const requestData = {
                ...formData,
                invoice_date: formatDateForDB(formData.invoice_date),
                due_date: formatDateForDB(formData.due_date)
            };

            const response = await fetch(`${API_BASE}/invoices/${editingInvoice.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update invoice');
            }

            if (data.success) {
                setShowEditModal(false);
                setEditingInvoice(null);
                fetchInvoices();
                fetchStats();
                alert(`Invoice updated successfully! Invoice Number: ${data.data.invoice_number || editingInvoice.invoice_number}`);
                resetForm();
            } else {
                throw new Error(data.message || 'Error updating invoice');
            }
        } catch (error) {
            handleApiError(error, 'Error updating invoice');
        } finally {
            setUpdating(false);
        }
    };

    // Function to close edit modal and reset form
    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingInvoice(null);
        resetForm();
    };

    // NEW: Check customer in Zoho Books
    const checkCustomerInZoho = async (customerId) => {
        try {
            setCheckingCustomer(customerId);
            setError(null);

            const response = await fetch(`${API_BASE}/invoices/customers/${customerId}/check-zoho`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setCustomerStatus(prev => ({
                    ...prev,
                    [customerId]: data.data
                }));

                // Update customer in the list
                setCustomers(prev => prev.map(customer =>
                    customer.id === customerId
                        ? {
                            ...customer,
                            zoho_contact_id: data.data.zoho_contact_id,
                            has_zoho_contact: data.data.exists_in_zoho,
                            needs_zoho_creation: !data.data.exists_in_zoho,
                            zoho_sync_status: data.data.exists_in_zoho ? 'synced' : 'pending'
                        }
                        : customer
                ));

                if (data.data.exists_in_zoho) {
                    alert(`✅ Customer found in Zoho Books!\nZoho Contact ID: ${data.data.zoho_contact_id}`);
                } else {
                    alert('⚠️ Customer not found in Zoho Books. You can create them using the "Auto Create Customer" button.');
                }

                return data.data;
            } else {
                throw new Error(data.message || 'Failed to check customer in Zoho');
            }
        } catch (error) {
            handleApiError(error, 'Failed to check customer in Zoho');
            return null;
        } finally {
            setCheckingCustomer(null);
        }
    };

    // NEW: Create customer in Zoho Books
    const createCustomerInZoho = async (customerId) => {
        try {
            setCreatingCustomer(customerId);
            setError(null);

            const response = await fetch(`${API_BASE}/invoices/customers/${customerId}/create-zoho`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                // Update customer in the list
                setCustomers(prev => prev.map(customer =>
                    customer.id === customerId
                        ? {
                            ...customer,
                            zoho_contact_id: data.data.zoho_contact_id,
                            has_zoho_contact: true,
                            needs_zoho_creation: false,
                            zoho_sync_status: 'synced'
                        }
                        : customer
                ));

                // Update customer status
                setCustomerStatus(prev => ({
                    ...prev,
                    [customerId]: {
                        exists_in_zoho: true,
                        zoho_contact_id: data.data.zoho_contact_id,
                        needs_creation: false,
                        customer: data.data.customer
                    }
                }));

                alert(`🎉 Customer created successfully in Zoho Books!\nZoho Contact ID: ${data.data.zoho_contact_id}`);
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to create customer in Zoho');
            }
        } catch (error) {
            handleApiError(error, 'Failed to create customer in Zoho');
            return null;
        } finally {
            setCreatingCustomer(null);
        }
    };

    // Fetch current organization
    const fetchCurrentOrganization = async () => {
        try {
            console.log('🔍 Fetching current organization...');
            const response = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders()
            });
            const result = await response.json();

            if (result.success && result.data.current_organization) {
                const org = result.data.current_organization;
                console.log('✅ Current organization fetched:', org);
                setCurrentOrganization(org);
                return org.organization_id;
            } else {
                console.error('❌ No current organization found');
                throw new Error('No current organization found');
            }
        } catch (error) {
            console.error('❌ Error fetching current organization:', error);
        }
    };

    // Initialize data
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                await fetchCurrentOrganization();
            } catch (error) {
                console.error('Failed to initialize component:', error);
            }
        };
        initializeComponent();
    }, []);

    // Add this function to fetch Zoho items
    const fetchZohoItems = async () => {
        try {
            console.log('🔍 Fetching Zoho items...');

            // FIXED: Call the correct endpoint without company_id parameter
            const response = await fetch(`${API_BASE}/invoices/zoho/items`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setZohoItems((data.data || []).map(item => ({
                    ...item,
                    id: item.id || item.item_id // use id if present, else item_id
                })));
                console.log('✅ Fetched Zoho items:', data.data?.length || 0);
            }
        } catch (error) {
            console.error('Error fetching Zoho items:', error);
            setZohoItems([]); // Set empty array on error
        }
    };

    // Call this when component mounts
    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchZohoItems();
        }
    }, [currentOrganization?.organization_id]);

    // FIXED: Proper UTC date formatting to avoid timezone issues
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';

        try {
            // Handle different date formats and check for invalid values
            if (typeof dateString !== 'string' || dateString.includes('NaN') || dateString === 'Invalid Date') {
                console.warn('Invalid date input:', dateString);
                return '';
            }

            let date;
            if (dateString.includes('T')) {
                // ISO format with time
                date = new Date(dateString);
            } else {
                // Date-only format - force local timezone
                date = new Date(dateString + 'T00:00:00');
            }

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date string:', dateString);
                return '';
            }

            // Format as YYYY-MM-DD
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error, dateString);
            return '';
        }
    };

    // FIXED: Convert date input to proper format for database
    const formatDateForDB = (dateString) => {
        if (!dateString) return '';

        try {
            // Check for invalid date strings
            if (typeof dateString !== 'string' || dateString.includes('NaN') || dateString === 'Invalid Date') {
                console.warn('Invalid date input for DB:', dateString);
                return '';
            }

            // Create date object from input (which is always YYYY-MM-DD)
            const date = new Date(dateString + 'T00:00:00'); // Force local timezone

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date for DB:', dateString);
                return '';
            }

            // Format as YYYY-MM-DD for database
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date for DB:', error, dateString);
            return '';
        }
    };

    // Get query string for API calls
    const getQueryString = (additionalParams = {}) => {
        const params = { ...filters, ...additionalParams, company_id: currentOrganization?.organization_id || filters.company_id };
        return new URLSearchParams(
            Object.entries(params).filter(([_, value]) => value !== '' && value !== null)
        ).toString();
    };

    // Error handling utility
    const handleApiError = (error, defaultMessage = 'An error occurred') => {
        console.error('API Error:', error);
        let errorMessage = defaultMessage;

        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            errorMessage = error.message;
        }

        setError(errorMessage);
        setTimeout(() => setError(null), 5000);
        return errorMessage;
    };

    // Fetch invoices with pagination and filters
    const fetchInvoices = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping load');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const queryString = getQueryString({ company_id: currentOrganization.organization_id });
            const response = await fetch(`${API_BASE}/invoices?${queryString}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Fetched invoice data:', data);

            if (data.success) {
                setInvoices(Array.isArray(data.data) ? data.data : []);
                setPagination(data.pagination || pagination);
            } else {
                throw new Error(data.message || 'Failed to fetch invoices');
            }
        } catch (error) {
            handleApiError(error, 'Failed to fetch invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch customers
    const fetchCustomers = async () => {
        try {
            const response = await fetch(`${API_BASE}/invoices/customers`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setCustomers(Array.isArray(data.data) ? data.data : []);
                console.log('✅ Fetched customers:', data.data?.length || 0);

                // Auto-fetch items for first customer if available
                if (data.data && data.data.length > 0 && !formData.company_id) {
                    setFormData(prev => ({
                        ...prev,
                        company_id: data.data[0].company_id
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
        }
    };

    // Fetch stats
    const fetchStats = async () => {
        try {
            const queryString = filters.currentOrganization?.organization_id ? `?company_id=${currentOrganization?.organization_id}` : '';
            const response = await fetch(`${API_BASE}/invoices/stats${queryString}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            console.log("📊 Stats Response:", data);

            if (data.success) {
                setStats(data.data || {});
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchInvoices();
            fetchStats();
        }
    }, [filters, currentOrganization?.organization_id]);

    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchCustomers();
        }
    }, [currentOrganization?.organization_id]);

    // Calculate due date based on payment terms
    useEffect(() => {
        if (formData.invoice_date && formData.payment_terms_days) {
            try {
                // Safely parse the invoice date
                const invoiceDateStr = formData.invoice_date;
                if (!invoiceDateStr || invoiceDateStr === '' || invoiceDateStr.includes('NaN')) {
                    console.warn('Invalid invoice date string:', invoiceDateStr);
                    return;
                }

                // Create date object with explicit local timezone
                const invoiceDate = new Date(invoiceDateStr + 'T00:00:00');

                // Check if date is valid
                if (isNaN(invoiceDate.getTime())) {
                    console.warn('Invalid invoice date:', invoiceDateStr);
                    return;
                }

                // Calculate due date
                const dueDate = new Date(invoiceDate);
                const paymentTerms = parseInt(formData.payment_terms_days);
                dueDate.setDate(dueDate.getDate() + paymentTerms);

                // Check if calculated due date is valid
                if (isNaN(dueDate.getTime())) {
                    console.warn('Invalid calculated due date');
                    return;
                }

                // Format the due date safely
                const year = dueDate.getFullYear();
                const month = String(dueDate.getMonth() + 1).padStart(2, '0');
                const day = String(dueDate.getDate()).padStart(2, '0');
                const formattedDueDate = `${year}-${month}-${day}`;

                setFormData(prev => ({
                    ...prev,
                    due_date: formattedDueDate
                }));
            } catch (error) {
                console.error('Error calculating due date:', error);
            }
        }
    }, [formData.invoice_date, formData.payment_terms_days]);

    // UPDATED: Create invoice with customer validation
    const createInvoice = async () => {
        try {
            setCreating(true);
            setError(null);

            // Validate required fields
            if (!formData.company_id || !formData.customer_name || !formData.line_items.length) {
                throw new Error('Please fill in all required fields');
            }

            // Validate line items
            for (let i = 0; i < formData.line_items.length; i++) {
                const item = formData.line_items[i];
                if (!item.item_name || !item.quantity || item.unit_price === undefined) {
                    throw new Error(`Please fill in all fields for line item ${i + 1}`);
                }
            }

            // NEW: Check if customer needs to be created in Zoho for auto-sync
            if (formData.auto_sync_zoho) {
                const selectedCustomer = customers.find(c => c.display_name === formData.customer_name);
                if (selectedCustomer && !selectedCustomer.has_zoho_contact) {
                    const userConfirm = confirm(
                        `The selected customer "${formData.customer_name}" is not yet created in Zoho Books.\n\n` +
                        `Would you like to automatically create them in Zoho Books first?`
                    );

                    if (userConfirm) {
                        const createResult = await createCustomerInZoho(selectedCustomer.id);
                        if (!createResult) {
                            throw new Error('Failed to create customer in Zoho Books. Please try again or disable auto-sync.');
                        }
                        // Customer created successfully, continue with invoice creation
                    } else {
                        // User declined, disable auto-sync and continue
                        setFormData(prev => ({ ...prev, auto_sync_zoho: false }));
                        alert('Auto-sync has been disabled. You can manually sync the invoice later after creating the customer in Zoho Books.');
                    }
                }
            }

            // FIXED: Format dates properly for database
            const requestData = {
                ...formData,
                invoice_date: formatDateForDB(formData.invoice_date),
                due_date: formatDateForDB(formData.due_date)
            };

            const response = await fetch(`${API_BASE}/invoices`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle specific error for customer creation needed
                if (data.needs_customer_creation) {
                    const userConfirm = confirm(
                        `The customer "${formData.customer_name}" needs to be created in Zoho Books first.\n\n` +
                        `Would you like to create them now?`
                    );

                    if (userConfirm) {
                        const createResult = await createCustomerInZoho(data.customer_id);
                        if (createResult) {
                            // Retry invoice creation
                            return await createInvoice();
                        }
                    }
                }
                throw new Error(data.message || 'Failed to create invoice');
            }

            if (data.success) {
                setShowCreateModal(false);
                fetchInvoices();
                fetchStats();
                alert(`Invoice created successfully! Invoice Number: ${data.data.invoice_number}`);
                resetForm();
            } else {
                throw new Error(data.message || 'Error creating invoice');
            }
        } catch (error) {
            handleApiError(error, 'Error creating invoice');
        } finally {
            setCreating(false);
        }
    };

    // Manual sync to Zoho
    const syncToZoho = async (invoiceId) => {
        try {
            // Find the invoice to check approval status
            const invoice = invoices.find(inv => inv.id === invoiceId);

            if (!canSync(invoice)) {
                const status = invoice.approverstatus || invoice.approvalInfo?.status || invoice.approver_status;
                let message = 'Cannot sync invoice - ';
                if (status === 'pending_approval') {
                    message += 'pending approval';
                } else if (status === 'rejected') {
                    message += 'rejected';
                } else {
                    message += 'not approved';
                }
                alert(message);
                return;
            }
            // Validate invoice ID
            if (!invoiceId || invoiceId === 'undefined') {
                throw new Error('Invalid invoice ID');
            }

            console.log('Syncing invoice ID:', invoiceId);
            setSyncing(invoiceId);
            setError(null);

            const response = await fetch(`${API_BASE}/invoices/${invoiceId}/sync`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            const data = await response.json();
            console.log('Sync response:', data);

            if (!response.ok) {
                throw new Error(data.message || 'Sync failed');
            }

            if (data.success) {
                fetchInvoices();
                fetchStats();
                alert('Invoice synced successfully to Zoho Books!');
            } else {
                throw new Error(data.message || 'Sync failed');
            }
        } catch (error) {
            handleApiError(error, 'Sync failed');
        } finally {
            setSyncing(null);
        }
    };

    // View invoice details
    const viewInvoiceDetails = async (invoiceId) => {
        try {
            const response = await fetch(`${API_BASE}/invoices/${invoiceId}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            if (data.success) {
                setSelectedInvoice(data.data);
                setShowDetailsModal(true);
            } else {
                throw new Error(data.message || 'Error fetching invoice details');
            }
        } catch (error) {
            handleApiError(error, 'Error fetching invoice details');
        }
    };

    // Update invoice status
    const updateInvoiceStatus = async (invoiceId, status) => {
        try {
            const response = await fetch(`${API_BASE}/invoices/${invoiceId}/status`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ status })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update status');
            }

            if (data.success) {
                fetchInvoices();
                fetchStats();
                alert(`Invoice status updated to ${status}`);
            } else {
                throw new Error(data.message || 'Error updating status');
            }
        } catch (error) {
            handleApiError(error, 'Error updating status');
        }
    };

    // Delete invoice
    const deleteInvoice = async (invoiceId) => {
        if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/invoices/${invoiceId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
                body: JSON.stringify({ company_id: formData.company_id })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete invoice');
            }

            if (data.success) {
                fetchInvoices();
                fetchStats();
                alert('Invoice deleted successfully');
            } else {
                throw new Error(data.message || 'Error deleting invoice');
            }
        } catch (error) {
            handleApiError(error, 'Error deleting invoice');
        }
    };

    // Reset form
    const resetForm = () => {
        // Get today's date safely
        const today = new Date();

        // Validate that today is a valid date
        if (isNaN(today.getTime())) {
            console.error('Invalid current date in resetForm');
            setFormData({
                company_id: '',
                customer_name: '',
                invoice_date: '',
                due_date: '',
                place_of_supply: '',
                payment_terms_days: 30,
                notes: '',
                terms_conditions: '',
                line_items: [
                    {
                        zoho_item_id: '',
                        item_name: '',
                        description: '',
                        quantity: 1,
                        unit_price: 0,
                        discount_percentage: 0,
                        tax_rate: 18
                    }
                ],
                auto_sync_zoho: false,
                send_notifications: false
            });
            return;
        }

        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayFormatted = `${year}-${month}-${day}`;

        setFormData({
            company_id: '',
            customer_name: '',
            invoice_date: todayFormatted, // Use safe date format
            due_date: '',
            place_of_supply: '',
            payment_terms_days: 30,
            notes: '',
            terms_conditions: '',
            line_items: [
                {
                    zoho_item_id: '',
                    item_name: '',
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    discount_percentage: 0,
                    tax_rate: 18
                }
            ],
            auto_sync_zoho: false,
            send_notifications: false
        });
    };

    // Add line item
    const addLineItem = () => {
        setFormData(prev => ({
            ...prev,
            line_items: [
                ...prev.line_items,
                {
                    zoho_item_id: '',
                    item_name: '',
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    discount_percentage: 0,
                    tax_rate: 18
                }
            ]
        }));
    };

    // Remove line item
    const removeLineItem = (index) => {
        if (formData.line_items.length > 1) {
            setFormData(prev => ({
                ...prev,
                line_items: prev.line_items.filter((_, i) => i !== index)
            }));
        }
    };

    // Update line item
    const updateLineItem = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            line_items: prev.line_items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    // Calculate totals
    const calculateTotals = () => {
        let subtotal = 0;
        let totalTax = 0;

        formData.line_items.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unit_price) || 0;
            const discountPercentage = parseFloat(item.discount_percentage) || 0;
            const taxRate = parseFloat(item.tax_rate) || 0;

            const lineTotal = quantity * unitPrice * (1 - discountPercentage / 100);
            subtotal += lineTotal;
            totalTax += lineTotal * taxRate / 100;
        });

        return {
            subtotal: subtotal.toFixed(2),
            totalTax: totalTax.toFixed(2),
            total: (subtotal + totalTax).toFixed(2)
        };
    };

    const totals = calculateTotals();

    // Handle pagination
    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    // Handle sorting
    const handleSort = (column) => {
        const newOrder = filters.sort_by === column && filters.sort_order === 'ASC' ? 'DESC' : 'ASC';
        setFilters(prev => ({ ...prev, sort_by: column, sort_order: newOrder, page: 1 }));
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const badges = {
            draft: { color: 'bg-gray-100 text-gray-800', icon: Edit },
            sent: { color: 'bg-blue-100 text-blue-800', icon: Send },
            paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle }
        };

        const badge = badges[status] || badges.draft;
        const IconComponent = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
                <IconComponent className="w-3 h-3" />
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    // Get sync status badge
    const getSyncStatusBadge = (syncStatus, zohoId) => {
        if (zohoId) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3" />
                    Synced
                </span>
            );
        }

        const badges = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
            synced: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            in_progress: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw }
        };

        const badge = badges[syncStatus] || badges.pending;
        const IconComponent = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
                <IconComponent className="w-3 h-3" />
                {syncStatus?.replace('_', ' ')}
            </span>
        );
    };

    // NEW: Get customer sync status indicator
    const getCustomerSyncStatus = (customer) => {
        if (customer.has_zoho_contact) {
            return (
                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" />
                    In Zoho
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                    <AlertCircle className="w-3 h-3" />
                    Not in Zoho
                </span>
            );
        }
    };

    // NEW: Render customer actions in dropdown
    const renderCustomerActions = (customer) => {
        if (customer.has_zoho_contact) {
            return (
                <div className="flex items-center gap-2 mt-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            checkCustomerInZoho(customer.id);
                        }}
                        disabled={checkingCustomer === customer.id}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 disabled:opacity-50"
                    >
                        {checkingCustomer === customer.id ? (
                            <>
                                <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
                                Checking...
                            </>
                        ) : (
                            <>
                                <Eye className="w-3 h-3 inline mr-1" />
                                Verify
                            </>
                        )}
                    </button>
                </div>
            );
        } else {
            return (
                <div className="flex items-center gap-2 mt-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            checkCustomerInZoho(customer.id);
                        }}
                        disabled={checkingCustomer === customer.id}
                        className="text-xs px-2 py-1 bg-yellow-50 text-yellow-600 rounded hover:bg-yellow-100 disabled:opacity-50"
                    >
                        {checkingCustomer === customer.id ? (
                            <>
                                <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
                                Checking...
                            </>
                        ) : (
                            <>
                                <Search className="w-3 h-3 inline mr-1" />
                                Check Zoho
                            </>
                        )}
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            createCustomerInZoho(customer.id);
                        }}
                        disabled={creatingCustomer === customer.id}
                        className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 disabled:opacity-50"
                    >
                        {creatingCustomer === customer.id ? (
                            <>
                                <RefreshCw className="w-3 h-3 animate-spin inline mr-1" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <UserPlus className="w-3 h-3 inline mr-1" />
                                Auto Create
                            </>
                        )}
                    </button>
                </div>
            );
        }
    };

    // Add this edit button to your actions column in the table (replace the existing actions td content)
    const renderActionButtons = (invoice) => {
        const isSynced = !!invoice.zohoinvoiceid;
        const canEditInvoice = canEdit(invoice);
        const canSyncInvoice = canSync(invoice);

        return (
            <div className="flex items-center space-x-2">

                {/* Edit button - enabled for all approved invoices (even synced ones) */}
                <button
                    onClick={() => openEditModal(invoice.id)}
                    disabled={!canEditInvoice}
                    className={canEditInvoice ? "text-purple-600 hover:text-purple-800" : "text-gray-400 cursor-not-allowed"}
                    title={!canEditInvoice ? "Cannot edit - approval required" : "Edit Invoice"}
                >
                    <Edit className="w-4 h-4" />
                </button>

                {/* Sync button - enabled only for approved, non-synced invoices */}
                <button
                    onClick={() => syncToZoho(invoice.id)}
                    disabled={syncing === invoice.id || !canSyncInvoice}
                    className={canSyncInvoice ? "text-green-600 hover:text-green-800" : "text-gray-400 cursor-not-allowed"}
                    title={
                        isSynced ? "Already synced to Zoho Books" :
                            !canSyncInvoice ? "Cannot sync - approval required" :
                                "Sync to Zoho"
                    }
                >
                    <RefreshCw className={`w-4 h-4 ${syncing === invoice.id ? 'animate-spin' : ''}`} />
                </button>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Header with Stats */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Invoice Management</h2>
                        <p className="text-gray-600">Create, manage and track your invoices with optional Zoho Books sync</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        Create Invoice
                    </button>
                </div>

                {/* Stats Grid */}
                {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8 text-blue-600" />
                            <div>
                                <p className="text-sm text-blue-600 font-medium">Total Invoices</p>
                                <p className="text-2xl font-bold text-blue-900">{stats.total_invoices || 0}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <DollarSign className="w-8 h-8 text-green-600" />
                            <div>
                                <p className="text-sm text-green-600 font-medium">Paid Amount</p>
                                <p className="text-2xl font-bold text-green-900">₹{parseFloat(stats.paid_amount || 0).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <Clock className="w-8 h-8 text-yellow-600" />
                            <div>
                                <p className="text-sm text-yellow-600 font-medium">Pending Amount</p>
                                <p className="text-2xl font-bold text-yellow-900">₹{parseFloat(stats.pending_amount || 0).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <RefreshCw className="w-8 h-8 text-purple-600" />
                            <div>
                                <p className="text-sm text-purple-600 font-medium">Synced</p>
                                <p className="text-2xl font-bold text-purple-900">{stats.synced_count || 0}</p>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Create Invoice Modal - UPDATED with customer actions */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
                        <div className="p-6 border-b">
                            <h3 className="text-xl font-semibold text-gray-900">Create New Invoice</h3>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Basic Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Customer <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.customer_name}
                                        onChange={(e) => {
                                            const customer = customers.find(c => c.display_name === e.target.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                customer_name: e.target.value,
                                                company_id: customer?.company_id || '',
                                                place_of_supply: customer?.place_of_supply || ''
                                            }));
                                        }}
                                        required
                                    >
                                        <option value="">Select Customer</option>
                                        {customers.map(customer => (
                                            <optgroup key={customer.id} label={`${customer.display_name} - ${customer.company_name}`}>
                                                <option value={customer.display_name}>
                                                    {customer.display_name} - {customer.company_name}
                                                </option>
                                            </optgroup>
                                        ))}
                                    </select>

                                    {/* NEW: Customer actions panel */}
                                    {formData.customer_name && (
                                        <div className="mt-2 p-3 bg-gray-50 rounded-md">
                                            {(() => {
                                                const selectedCustomer = customers.find(c => c.display_name === formData.customer_name);
                                                if (!selectedCustomer) return null;

                                                return (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-medium text-gray-700">
                                                                Zoho Books Status:
                                                            </span>
                                                            {getCustomerSyncStatus(selectedCustomer)}
                                                        </div>

                                                        {selectedCustomer.zoho_contact_id && (
                                                            <div className="text-xs text-gray-600">
                                                                Zoho Contact ID: {selectedCustomer.zoho_contact_id}
                                                            </div>
                                                        )}

                                                        {/* Customer Action Buttons */}
                                                        <div className="flex items-center gap-2">
                                                            {!selectedCustomer.has_zoho_contact ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => checkCustomerInZoho(selectedCustomer.id)}
                                                                        disabled={checkingCustomer === selectedCustomer.id}
                                                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-yellow-50 text-yellow-600 border border-yellow-200 rounded hover:bg-yellow-100 disabled:opacity-50"
                                                                    >
                                                                        {checkingCustomer === selectedCustomer.id ? (
                                                                            <>
                                                                                <RefreshCw className="w-3 h-3 animate-spin" />
                                                                                Checking...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Search className="w-3 h-3" />
                                                                                Check in Zoho
                                                                            </>
                                                                        )}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => createCustomerInZoho(selectedCustomer.id)}
                                                                        disabled={creatingCustomer === selectedCustomer.id}
                                                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-600 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
                                                                    >
                                                                        {creatingCustomer === selectedCustomer.id ? (
                                                                            <>
                                                                                <RefreshCw className="w-3 h-3 animate-spin" />
                                                                                Creating...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <UserPlus className="w-3 h-3" />
                                                                                Auto Create Customer
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => checkCustomerInZoho(selectedCustomer.id)}
                                                                    disabled={checkingCustomer === selectedCustomer.id}
                                                                    className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
                                                                >
                                                                    {checkingCustomer === selectedCustomer.id ? (
                                                                        <>
                                                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                                                            Verifying...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Eye className="w-3 h-3" />
                                                                            Verify in Zoho
                                                                        </>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Auto-sync warning */}
                                                        {!selectedCustomer.has_zoho_contact && formData.auto_sync_zoho && (
                                                            <div className="bg-amber-50 border border-amber-200 rounded p-2">
                                                                <div className="flex items-start gap-2">
                                                                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                                                                    <div className="text-xs text-amber-800">
                                                                        <p className="font-medium">Auto-sync enabled</p>
                                                                        <p>This customer needs to be created in Zoho Books first. Use "Auto Create Customer" button above or disable auto-sync.</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Invoice Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.invoice_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (Days)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.payment_terms_days}
                                        onChange={(e) => setFormData(prev => ({ ...prev, payment_terms_days: e.target.value }))}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Due Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                        required
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Place of Supply</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.place_of_supply}
                                        onChange={(e) => setFormData(prev => ({ ...prev, place_of_supply: e.target.value }))}
                                        placeholder="Enter place of supply"
                                    />
                                </div>
                            </div>

                            {/* Line Items Section - rest of the form remains the same */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-lg font-medium text-gray-900">
                                        Line Items <span className="text-red-500">*</span>
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={addLineItem}
                                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Item
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {formData.line_items.map((item, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-4">
                                                <h5 className="text-sm font-medium text-gray-700">Item {index + 1}</h5>
                                                {formData.line_items.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLineItem(index)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <div className="lg:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Item Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.item_name}
                                                        onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                                                        placeholder="Enter item name"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Quantity <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.quantity}
                                                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Unit Price (₹) <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.unit_price}
                                                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.discount_percentage}
                                                        onChange={(e) => updateLineItem(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.tax_rate}
                                                        onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                                    >
                                                        <option value="0">0%</option>
                                                        <option value="5">5%</option>
                                                        <option value="12">12%</option>
                                                        <option value="18">18%</option>
                                                        <option value="28">28%</option>
                                                    </select>
                                                </div>

                                                <div className="lg:col-span-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                                    <textarea
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows="2"
                                                        value={item.description}
                                                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                                        placeholder="Enter item description"
                                                    />
                                                </div>
                                            </div>

                                            {/* Line Total */}
                                            <div className="mt-4 p-3 bg-gray-50 rounded-md">
                                                <div className="text-sm text-gray-600">
                                                    Line Total: ₹{(
                                                        (parseFloat(item.quantity) || 0) *
                                                        (parseFloat(item.unit_price) || 0) *
                                                        (1 - (parseFloat(item.discount_percentage) || 0) / 100) *
                                                        (1 + (parseFloat(item.tax_rate) || 0) / 100)
                                                    ).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Invoice Totals */}
                                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Subtotal:</span>
                                            <span className="text-sm font-medium">₹{totals.subtotal}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Total Tax:</span>
                                            <span className="text-sm font-medium">₹{totals.totalTax}</span>
                                        </div>
                                        <div className="flex justify-between border-t pt-2">
                                            <span className="text-lg font-semibold text-gray-900">Total:</span>
                                            <span className="text-lg font-bold text-blue-600">₹{totals.total}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="3"
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Enter invoice notes"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="3"
                                        value={formData.terms_conditions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                                        placeholder="Enter terms and conditions"
                                    />
                                </div>
                            </div>

                            {/* Options */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="auto_sync_zoho"
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        checked={formData.auto_sync_zoho}
                                        onChange={(e) => setFormData(prev => ({ ...prev, auto_sync_zoho: e.target.checked }))}
                                    />
                                    <label htmlFor="auto_sync_zoho" className="text-sm text-gray-700">
                                        <span className="flex items-center gap-2">
                                            <Zap className="w-4 h-4 text-yellow-500" />
                                            Automatically sync to Zoho Books (customer must exist in Zoho first)
                                        </span>
                                    </label>
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="send_notifications"
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        checked={formData.send_notifications}
                                        onChange={(e) => setFormData(prev => ({ ...prev, send_notifications: e.target.checked }))}
                                    />
                                    <label htmlFor="send_notifications" className="text-sm text-gray-700">
                                        Send email and WhatsApp notifications (optional)
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={creating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={createInvoice}
                                disabled={creating || !formData.customer_name || formData.line_items.length === 0 || !formData.company_id}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {creating ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Create Invoice
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rest of the component remains the same - filters, table, etc. */}
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-64">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search invoices..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                    </div>

                    <select
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    <select
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={filters.limit}
                        onChange={(e) => handleFilterChange('limit', e.target.value)}
                    >
                        <option value="10">10 per page</option>
                        <option value="20">20 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                    </select>

                    <button
                        onClick={fetchInvoices}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('invoice_number')}
                                >
                                    Invoice Details
                                    {filters.sort_by === 'invoice_number' && (
                                        <span className="ml-1">{filters.sort_order === 'ASC' ? '↑' : '↓'}</span>
                                    )}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('total_value')}
                                >
                                    Amount
                                    {filters.sort_by === 'total_value' && (
                                        <span className="ml-1">{filters.sort_order === 'ASC' ? '↑' : '↓'}</span>
                                    )}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sync Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8">
                                        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                                        <span className="text-gray-500">Loading invoices...</span>
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8">
                                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500">No invoices found</p>
                                        <p className="text-sm text-gray-400">Try adjusting your filters or create a new invoice</p>
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((invoice) => (
                                    <tr key={`invoice-${invoice.id}-${invoice.updated_at || invoice.created_at}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {invoice.invoice_number || `#${invoice.id}`}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Date: {new Date(invoice.invoice_date).toLocaleDateString()}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Due: {new Date(invoice.due_date).toLocaleDateString()}
                                            </div>
                                        </td>

                                        <td className="px-4 py-4">
                                            <div className="text-sm text-gray-900">{invoice.customer_name}</div>
                                            {invoice.customer_email && (
                                                <div className="text-xs text-gray-500">{invoice.customer_email}</div>
                                            )}
                                            {invoice.customer_phone && (
                                                <div className="text-xs text-gray-500">{invoice.customer_phone}</div>
                                            )}
                                        </td>

                                        <td className="px-4 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                ₹{(() => {
                                                    // Calculate total if total_value is empty or zero
                                                    const totalValue = parseFloat(invoice.amountValue || 0);
                                                    if (totalValue > 0) {
                                                        return totalValue.toLocaleString();
                                                    }

                                                    // Calculate from amount_value + taxes
                                                    const amountValue = parseFloat(invoice.amount_value || 0);
                                                    const cgstRate = parseFloat(invoice.cgst_rate || 0);
                                                    const sgstRate = parseFloat(invoice.sgst_rate || 0);
                                                    const igstRate = parseFloat(invoice.igst_rate || 0);

                                                    const cgstAmount = (amountValue * cgstRate) / 100;
                                                    const sgstAmount = (amountValue * sgstRate) / 100;
                                                    const igstAmount = (amountValue * igstRate) / 100;

                                                    const calculatedTotal = amountValue + cgstAmount + sgstAmount + igstAmount;
                                                    return calculatedTotal.toLocaleString();
                                                })()}
                                            </div>
                                            {invoice.line_items_count && (
                                                <div className="text-xs text-gray-500">
                                                    {invoice.line_items_count} item{invoice.line_items_count > 1 ? 's' : ''}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">
                                                Base: ₹{parseFloat(invoice.amount_value || 0).toLocaleString()}
                                            </div>
                                        </td>

                                        <td className="px-4 py-4">
                                            {getStatusBadge(invoice.invoice_status)}
                                        </td>

                                        <td className="px-4 py-4">
                                            {getSyncStatusBadge(invoice.zoho_sync_status, invoice.zoho_invoice_id)}
                                            {invoice.zoho_invoice_id && (
                                                <div className="text-xs text-gray-500 mt-1 font-mono">
                                                    ID: {invoice.zoho_invoice_id}
                                                </div>
                                            )}
                                            {invoice.zoho_sync_error && (
                                                <div className="text-xs text-red-600 mt-1">
                                                    Error: {invoice.zoho_sync_error.substring(0, 50)}...
                                                </div>
                                            )}
                                        </td>

                                        <td className="px-4 py-4">
                                            {getApprovalStatusDisplay(invoice)}
                                        </td>

                                        <td className="px-4 py-4">
                                            {renderActionButtons(invoice)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Showing {((pagination.current_page - 1) * pagination.limit) + 1} to{' '}
                                {Math.min(pagination.current_page * pagination.limit, pagination.total_records)} of{' '}
                                {pagination.total_records} results
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={!pagination.has_previous}
                                    className="flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Previous
                                </button>

                                <span className="text-sm text-gray-700">
                                    Page {pagination.current_page} of {pagination.total_pages}
                                </span>

                                <button
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={!pagination.has_next}
                                    className="flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Edit Invoice Modal */}
                {showEditModal && editingInvoice && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
                            <div className="p-6 border-b">
                                <h3 className="text-xl font-semibold text-gray-900">
                                    Edit Invoice {editingInvoice.invoice_number || `#${editingInvoice.id}`}
                                </h3>
                                <p className="text-gray-600">
                                    Update invoice details and line items
                                </p>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Basic Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Customer <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.customer_name}
                                            onChange={(e) => {
                                                const customer = customers.find(c => c.display_name === e.target.value);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    customer_name: e.target.value,
                                                    company_id: customer?.company_id || prev.company_id,
                                                    place_of_supply: customer?.place_of_supply || prev.place_of_supply
                                                }));
                                            }}
                                            required
                                        >
                                            <option value="">Select Customer</option>
                                            {customers.map(customer => (
                                                <option key={customer.id} value={customer.display_name}>
                                                    {customer.display_name} - {customer.company_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Invoice Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.invoice_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (Days)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.payment_terms_days}
                                            onChange={(e) => setFormData(prev => ({ ...prev, payment_terms_days: e.target.value }))}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Due Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.due_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Place of Supply</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.place_of_supply}
                                            onChange={(e) => setFormData(prev => ({ ...prev, place_of_supply: e.target.value }))}
                                            placeholder="Enter place of supply"
                                        />
                                    </div>
                                </div>

                                {/* Line Items */}
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-lg font-medium text-gray-900">
                                            Line Items <span className="text-red-500">*</span>
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={addLineItem}
                                            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Item
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {formData.line_items.map((item, index) => (
                                            <div key={`edit-line-item-${index}`} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex justify-between items-start mb-4">
                                                    <h5 className="text-sm font-medium text-gray-700">Item {index + 1}</h5>
                                                    {formData.line_items.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeLineItem(index)}
                                                            className="text-red-600 hover:text-red-800"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    <div className="lg:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Item Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={item.item_name}
                                                            onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                                                            placeholder="Enter item name"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Quantity <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0.01"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={item.quantity}
                                                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Unit Price (₹) <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={item.unit_price}
                                                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={item.discount_percentage}
                                                            onChange={(e) => updateLineItem(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                                                        <select
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={item.tax_rate}
                                                            onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                                        >
                                                            <option value="0">0%</option>
                                                            <option value="5">5%</option>
                                                            <option value="12">12%</option>
                                                            <option value="18">18%</option>
                                                            <option value="28">28%</option>
                                                        </select>
                                                    </div>

                                                    <div className="lg:col-span-3">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                                        <textarea
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            rows="2"
                                                            value={item.description}
                                                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                                            placeholder="Enter item description"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Line Total */}
                                                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                                                    <div className="text-sm text-gray-600">
                                                        Line Total: ₹{(
                                                            (parseFloat(item.quantity) || 0) *
                                                            (parseFloat(item.unit_price) || 0) *
                                                            (1 - (parseFloat(item.discount_percentage) || 0) / 100) *
                                                            (1 + (parseFloat(item.tax_rate) || 0) / 100)
                                                        ).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Invoice Totals */}
                                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-gray-600">Subtotal:</span>
                                                <span className="text-sm font-medium">₹{totals.subtotal}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-gray-600">Total Tax:</span>
                                                <span className="text-sm font-medium">₹{totals.totalTax}</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-2">
                                                <span className="text-lg font-semibold text-gray-900">Total:</span>
                                                <span className="text-lg font-bold text-blue-600">₹{totals.total}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                        <textarea
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="3"
                                            value={formData.notes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="Enter invoice notes"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                                        <textarea
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="3"
                                            value={formData.terms_conditions}
                                            onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                                            placeholder="Enter terms and conditions"
                                        />
                                    </div>
                                </div>

                                {/* Options */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="auto_sync_zoho_edit"
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            checked={formData.auto_sync_zoho}
                                            onChange={(e) => setFormData(prev => ({ ...prev, auto_sync_zoho: e.target.checked }))}
                                        />
                                        <label htmlFor="auto_sync_zoho_edit" className="text-sm text-gray-700">
                                            Automatically sync to Zoho Books (optional)
                                        </label>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="send_notifications_edit"
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            checked={formData.send_notifications}
                                            onChange={(e) => setFormData(prev => ({ ...prev, send_notifications: e.target.checked }))}
                                        />
                                        <label htmlFor="send_notifications_edit" className="text-sm text-gray-700">
                                            Send email and WhatsApp notifications (optional)
                                        </label>
                                    </div>
                                </div>

                                {/* Warning for synced invoices */}
                                {editingInvoice.zoho_invoice_id && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="w-5 h-5 text-amber-600" />
                                            <div>
                                                <p className="text-amber-800 font-medium">
                                                    Warning: This invoice is already synced to Zoho Books
                                                </p>
                                                <p className="text-amber-700 text-sm">
                                                    Changes made here will not automatically update in Zoho Books. You may need to manually sync again or update directly in Zoho Books.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Actions */}
                            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                                <button
                                    onClick={closeEditModal}
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    disabled={updating}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={updateInvoice}
                                    disabled={updating || !formData.customer_name || formData.line_items.length === 0 || !formData.company_id}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {updating ? (
                                        <>
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <Edit className="w-4 h-4" />
                                            Update Invoice
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceManagement;